import React from 'react';
import { Link } from 'react-router-dom';

const SITE_ORIGIN = 'https://www.cognidoor.com';

const INTERNAL_PATHS = {
  [`${SITE_ORIGIN}/terms-of-use`]: '/terms-of-use',
  [`${SITE_ORIGIN}/privacy-policy`]: '/privacy-policy',
  [`${SITE_ORIGIN}/refund-policy`]: '/refund-policy',
  [`${SITE_ORIGIN}/contact`]: '/contact',
};

const URL_REGEX = /https?:\/\/[^\s)]+/;
const EMAIL_REGEX = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
const BOLD_REGEX = /\*\*(.+?)\*\*/;
const PARSE_INLINE_MAX_ITERATIONS = 500;

function renderLink(href, label, key) {
  const internalPath = INTERNAL_PATHS[href];
  const className = 'text-blue-700 underline hover:text-blue-900';
  if (internalPath) {
    return (
      <Link key={key} to={internalPath} className={className}>
        {label}
      </Link>
    );
  }
  if (href.startsWith('mailto:')) {
    return (
      <a key={key} href={href} className={className}>
        {label}
      </a>
    );
  }
  return (
    <a key={key} href={href} className={className} rel="noopener noreferrer">
      {label}
    </a>
  );
}

export function parseInline(text) {
  const nodes = [];
  let remaining = text;
  let key = 0;
  let iterations = 0;

  while (remaining.length > 0) {
    if (iterations >= PARSE_INLINE_MAX_ITERATIONS) {
      nodes.push(remaining);
      break;
    }
    iterations += 1;

    const boldMatch = remaining.match(BOLD_REGEX);
    const urlMatch = remaining.match(URL_REGEX);
    const emailMatch = remaining.match(EMAIL_REGEX);

    const candidates = [
      boldMatch && boldMatch.index != null
        ? { type: 'bold', match: boldMatch, index: boldMatch.index }
        : null,
      urlMatch && urlMatch.index != null
        ? { type: 'url', match: urlMatch, index: urlMatch.index }
        : null,
      emailMatch && emailMatch.index != null
        ? { type: 'email', match: emailMatch, index: emailMatch.index }
        : null,
    ].filter(Boolean);

    if (candidates.length === 0) {
      nodes.push(remaining);
      break;
    }

    candidates.sort((a, b) => a.index - b.index);
    const next = candidates[0];
    const { match, index, type } = next;
    const previousLength = remaining.length;

    if (index > 0) {
      nodes.push(remaining.slice(0, index));
    }

    if (type === 'bold') {
      nodes.push(<strong key={`b-${key++}`}>{match[1]}</strong>);
      remaining = remaining.slice(index + match[0].length);
    } else if (type === 'url') {
      const href = match[0];
      nodes.push(renderLink(href, href, `u-${key++}`));
      remaining = remaining.slice(index + href.length);
    } else if (type === 'email') {
      const email = match[0];
      nodes.push(renderLink(`mailto:${email}`, email, `e-${key++}`));
      remaining = remaining.slice(index + email.length);
    }

    if (remaining.length >= previousLength) {
      nodes.push(remaining);
      break;
    }
  }

  return nodes;
}

function isListItem(line) {
  return /^-\s/.test(line) || /^\d+\.\s/.test(line);
}

function getListItemText(line) {
  if (/^-\s/.test(line)) return line.slice(2);
  return line.replace(/^\d+\.\s/, '');
}

export function parseLegalMarkdown(markdown) {
  const lines = markdown.split('\n');
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith('# ')) {
      blocks.push({ type: 'h1', text: line.slice(2).trim() });
      i += 1;
      continue;
    }

    if (line.startsWith('## ')) {
      blocks.push({ type: 'h2', text: line.slice(3).trim() });
      i += 1;
      continue;
    }

    if (line.startsWith('### ')) {
      blocks.push({ type: 'h3', text: line.slice(4).trim() });
      i += 1;
      continue;
    }

    if (isListItem(line)) {
      const ordered = /^\d+\.\s/.test(line);
      const items = [];
      while (i < lines.length && isListItem(lines[i])) {
        items.push(getListItemText(lines[i]));
        i += 1;
      }
      blocks.push({ type: ordered ? 'ol' : 'ul', items });
      continue;
    }

    if (line.trim() === '') {
      i += 1;
      continue;
    }

    const paragraphLines = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !lines[i].startsWith('#') &&
      !isListItem(lines[i])
    ) {
      paragraphLines.push(lines[i]);
      i += 1;
    }
    blocks.push({ type: 'p', text: paragraphLines.join(' ') });
  }

  return blocks;
}

export function extractLegalMeta(markdown) {
  const lines = markdown.split('\n').slice(1, 8);
  const meta = [];

  for (const line of lines) {
    const match = line.match(/^\*\*(.+?):\*\*\s*(.+)$/);
    if (match) {
      meta.push({ label: match[1], value: match[2].trim() });
    }
  }

  return meta;
}

export function renderLegalBlock(block, index) {
  switch (block.type) {
    case 'h1':
      return null;
    case 'h2':
      return (
        <h2
          key={index}
          className="mt-8 mb-3 text-xl font-serif font-semibold text-slate-900"
        >
          {parseInline(block.text)}
        </h2>
      );
    case 'h3':
      return (
        <h3
          key={index}
          className="mt-6 mb-2 text-lg font-serif font-semibold text-slate-900"
        >
          {parseInline(block.text)}
        </h3>
      );
    case 'p':
      return (
        <p key={index} className="mb-4">
          {parseInline(block.text)}
        </p>
      );
    case 'ul':
      return (
        <ul key={index} className="mb-4 list-disc space-y-2 pl-6">
          {block.items.map((item, itemIndex) => (
            <li key={itemIndex}>{parseInline(item)}</li>
          ))}
        </ul>
      );
    case 'ol':
      return (
        <ol key={index} className="mb-4 list-decimal space-y-2 pl-6">
          {block.items.map((item, itemIndex) => (
            <li key={itemIndex}>{parseInline(item)}</li>
          ))}
        </ol>
      );
    default:
      return null;
  }
}

export function renderLegalMarkdown(markdown) {
  const blocks = parseLegalMarkdown(markdown);
  return blocks.map((block, index) => renderLegalBlock(block, index));
}
